import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { Alert, Group, Stack } from '@mantine/core'
import { useNavigate } from 'react-router-dom'
import ExportToCsv from 'app/views/components/ExportToCsv/ExportToCsv'
import MainTable from 'app/views/components/Table/MainTable'
import KPI_Section from 'app/views/components/KPI/KPI-horizontal-section'
import { ServicingParams } from 'app/models/servicing-params'
import {
  getServicingLoadError,
  getServicingLoader,
  getServicingRows,
} from 'app/state/ducks/servicing/selectors'
import {
  GridColumnVisibilityStorage,
  GridFilterStorage,
  GridPaginationStorage,
  GridSortingStorage,
  useGridColumnVisibility,
  useGridFilters,
  useGridPagination,
  useGridSorting,
} from 'app/utils/grid-sorting-storage'
import { ServicingCols } from './servicing-columnDefs'
import { getKPIData } from './servicing-kpi-data'
import { handleServicingRowClicked } from './servicing-navigation'

interface props {
  tab?: string
}

/**
 * The Servicing table. Rows come from GET /api/servicing/events, built and
 * labelled server-side; the tab filters the loaded feed locally. The fee
 * rows' amounts arrive a moment later from the pricing read.
 */
export function ServicingPageData({ tab }: props) {
  const { sorting, handleSortingChange } = useGridSorting(
    GridSortingStorage.servicing,
  )
  const { toMRTColumnFilters, handleMRTFiltersChange } = useGridFilters(
    GridFilterStorage.servicing,
  )
  const { pagination, handlePaginationChange } = useGridPagination(
    GridPaginationStorage.servicing,
  )
  const { columnVisibility, handleColumnVisibilityChange } =
    useGridColumnVisibility(GridColumnVisibilityStorage.servicing)
  const navigate = useNavigate()
  const rows = useSelector(getServicingRows)
  const isLoading = useSelector(getServicingLoader)
  const loadError = useSelector(getServicingLoadError)

  const servicingData = useMemo<ServicingParams[]>(
    () => (tab ? rows.filter(row => row.scheduled === tab) : rows),
    [rows, tab],
  )
  const [filteredServicingData, setFilteredServicingData] =
    useState<ServicingParams[]>(servicingData)
  useEffect(() => {
    setFilteredServicingData(servicingData)
  }, [servicingData])
  const kpiData = getKPIData(servicingData, setFilteredServicingData)

  // CSV-only shape: Event Date in YYYY-MM-DD plus the 5 facility identifiers.
  const csvExportData = useMemo(
    () =>
      filteredServicingData.map(row => ({
        'Event Date': row.effectiveDate
          ? moment(row.effectiveDate).format('YYYY-MM-DD')
          : '',
        'Deal Name': row.dealName,
        'Deal ID': row.dealId ?? '',
        'Facility Name': row.facilityName,
        'Facility ID': row.facilityId ?? '',
        'Client Facility ID': row.clientFacilityId ?? '',
        'Facility CUSIP': row.facilityCusip ?? '',
        'Facility LXID': row.facilityLxId ?? '',
        Stage: row.stage,
        Transaction: row.transaction,
        Amount: row.amount,
        Currency: row.currency,
        Notices: row.notices,
        Admin: Array.isArray(row.admin) ? row.admin.join(', ') : row.admin,
      })),
    [filteredServicingData],
  )

  return (
    <>
      {loadError && (
        <Alert color="red" title="Servicing feed unavailable">
          {loadError}
        </Alert>
      )}
      <Stack>
        <KPI_Section kpiData={kpiData} isLoading={isLoading} />
      </Stack>
      <MainTable
        tableName="Servicing Table"
        withPagination={true}
        csvExportEnabled={false}
        enableTopToolbar={false}
        headerBackgroundColor="#F0EEEE"
        overflow="auto"
        minHeight="0px"
        columnDefs={ServicingCols}
        data={filteredServicingData}
        isLoading={isLoading}
        initialSorting={[
          { id: sorting.sortBy, desc: sorting.order === 'desc' },
        ]}
        onSortingChange={handleSortingChange}
        initialColumnFilters={toMRTColumnFilters()}
        onColumnFiltersChange={handleMRTFiltersChange}
        initialPagination={pagination}
        onPaginationChange={handlePaginationChange}
        initialColumnVisibility={columnVisibility}
        onColumnVisibilityChange={handleColumnVisibilityChange}
        setRow={(e: ServicingParams, cellName: string) => {
          if (cellName != 'id') handleServicingRowClicked(e, navigate)
        }}
      />
      <Group position="left" mt="xs">
        <ExportToCsv
          csvName="Servicing Table"
          exportObj={csvExportData}
          style={{
            width: '150px',
            height: '35px',
            backgroundColor: '#32439A',
            color: '#fff',
          }}
        />
      </Group>
    </>
  )
}
